import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import api from '../../services/miscellaneousReceiptRAApi';
import { fmt3, round3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { parseApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';

const fmtDate = (value) => {
    const d = parseApiDate(value);
    return d ? moment(d).format('DD-MMM-YYYY') : '';
};

const ReadOnly = ({ label, value }) => (
    <div>
        <div className="text-muted small">{label}</div>
        <div className="fw-semibold">{value || '---'}</div>
    </div>
);

/** Read-only view of one receipt. Receipts are reversed through the Reverse Receipt module, so there is no edit or delete. */
const MiscellaneousReceiptRAViewDialog = ({ receiptNo, onClose }) => {
    const [state, setState] = useState({ loading: true, error: '', detail: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await api.getReceiptByNo(receiptNo);
            if (cancelled) return;
            setState(res.ok
                ? { loading: false, error: '', detail: res.result }
                : { loading: false, error: res.message, detail: null });
        })();
        return () => { cancelled = true; };
    }, [receiptNo]);

    const { loading, error, detail } = state;
    const h = detail?.header;
    const lines = detail?.lines || [];
    const total = round3(lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0));

    return (
        <Modal show onHide={onClose} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title className="h6">Miscellaneous Receipt {receiptNo}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && <div className="text-center p-4">Loading...</div>}
                {!loading && error && <div className="alert alert-danger mb-0">{error}</div>}
                {!loading && h && (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' }}>
                            <ReadOnly label="Receipt No" value={h.receiptNo} />
                            <ReadOnly label="Receipt Date" value={fmtDate(h.paymentDate) || h.paymentDate} />
                            <ReadOnly label="Receipt Type" value={h.receiptType} />
                            <ReadOnly label="Transaction Type" value={h.transactionType} />
                            <ReadOnly label="Tenant" value={[h.tenantNo, h.tenantName].filter(Boolean).join(' - ')} />
                            <ReadOnly label="Property" value={h.propertyName || h.propertyNo} />
                            <ReadOnly label="Unit No" value={h.unitNo} />
                            <ReadOnly label="Lease No" value={h.leaseNo} />
                            <ReadOnly label="Group" value={h.groupId} />
                            <ReadOnly label="Cheque No" value={h.chequeNo} />
                            <ReadOnly label="Drawn On Bank" value={h.drawnOnBank} />
                            <ReadOnly label="Cheque Date" value={fmtDate(h.chequeDate)} />
                            <ReadOnly label="Card Type" value={h.cardType} />
                            <ReadOnly label="Card No" value={h.cardNo} />
                            <ReadOnly label="Debit Account" value={h.debitAccountName || h.debitAccountCode} />
                            <ReadOnly label="Ref No" value={h.refNo} />
                            <ReadOnly label="Voucher No" value={h.voucherNo} />
                            <ReadOnly label="Total Amount" value={fmt3(h.totalAmount)} />
                            <ReadOnly label="Created By" value={h.modifiedBy} />
                            <ReadOnly label="Created On" value={h.modifiedOn ? moment(h.modifiedOn).format('DD-MMM-YYYY HH:mm') : ''} />
                        </div>
                        <div className="mt-3">
                            <ReadOnly label="Narration" value={h.narration} />
                        </div>
                        <div className="table-responsive mt-3">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr style={{ background: '#6E5199', color: '#fff' }}>
                                        <th>Payment Type</th>
                                        <th>Credit Account</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l) => (
                                        <tr key={l.recordId || `${l.paymentTypeDesc}-${l.creditAccountCode}`}>
                                            <td>{l.paymentTypeDesc}</td>
                                            <td>{[l.creditAccountCode, l.creditAccountName].filter(Boolean).join(' - ')}</td>
                                            <td className="text-end">{fmt3(l.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="fw-bold">
                                        <td colSpan={2}>Total</td>
                                        <td className="text-end">{fmt3(total)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="mainbtnSecondary" onClick={onClose}><div>Close</div></button>
            </Modal.Footer>
        </Modal>
    );
};

export default MiscellaneousReceiptRAViewDialog;
